import { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Typography } from "@mui/material";
import { Download as DownloadIcon, ShoppingCart, FilterList } from "@mui/icons-material";
import api from "../../../api";
import downloadPaperPdf from "../../../components/scan/downloadPaperPdf";

/**
 * "Practice paper packs for your target schools": the school's PDF pack (Bundle Pack, resource_type 6)
 * for each of the student's target schools. Owned → filter the catalogue to that school's
 * papers / download every PDF as a ZIP. Not owned → into the panel basket, which buys
 * bundles through the same checkout as single papers.
 */
export default function SchoolPackStrip({ targetSchools, refreshKey = 0, basketItems, onAddToBasket, onShowPapers }) {
  const [packs, setPacks] = useState([]);
  const [downloading, setDownloading] = useState(null);
  const schoolIds = (targetSchools || []).map((s) => s.id).filter(Boolean);
  const schoolKey = schoolIds.slice().sort().join(",");

  useEffect(() => {
    if (!schoolKey) {
      setPacks([]);
      return;
    }
    api
      .get("/pdf-packs", { params: { school_ids: schoolKey } })
      .then((res) => {
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        // A school can have several packs (e.g. HBS Round 2 sets); the school mock pack is
        // the one with online papers. Bought ones first.
        setPacks(
          list
            .filter((p) => (p.online_papers || []).length > 0)
            .sort((a, b) => Number(b.already_owned || b.covered_by_plan) - Number(a.already_owned || a.covered_by_plan))
        );
      })
      .catch(() => setPacks([]));
  }, [schoolKey, refreshKey]);

  if (packs.length === 0) return null;

  const inBasket = (pack) =>
    (basketItems || []).some((row) => row.resourceType === "bundle" && Number(row.resourceId) === Number(pack.id));

  const downloadAll = async (pack) => {
    setDownloading(pack.id);
    try {
      await downloadPaperPdf(`/pdf-packs/${encodeURIComponent(pack.slug)}/download-all`, `${pack.slug}.zip`);
    } finally {
      setDownloading(null);
    }
  };

  const until = (iso) =>
    iso ? new Date(iso).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
        Practice paper packs for your target schools
      </Typography>
      <Grid container spacing={2}>
        {packs.map((pack) => {
          const owned = pack.already_owned || pack.covered_by_plan;
          const papers = (pack.online_papers || []).length;
          return (
            <Grid item xs={12} sm={6} md={4} key={pack.id}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: 2,
                  border: owned ? "2px solid #1E7A5F" : "1px solid #e0e0e0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
              >
                <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.25, height: "100%", boxSizing: "border-box" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                    <Chip
                      size="small"
                      label={
                        owned
                          ? pack.covered_by_plan && !pack.already_owned
                            ? pack.access_label || "In your plan"
                            : `Owned${pack.expires_at ? ` · until ${until(pack.expires_at)}` : ""}`
                          : "Target school"
                      }
                      sx={{
                        fontWeight: 800,
                        fontSize: "0.68rem",
                        height: 22,
                        bgcolor: owned ? "#E7F4EF" : "#FDECEB",
                        color: owned ? "#1E7A5F" : "#D9251A",
                      }}
                    />
                    {!owned && (
                      <Typography sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{pack.price_display}</Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontWeight: 800, lineHeight: 1.3 }}>{pack.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {papers} online paper{papers === 1 ? "" : "s"} · {pack.file_count} PDFs with mark schemes
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mt: "auto", flexWrap: "wrap" }}>
                    {owned ? (
                      <>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<FilterList />}
                          onClick={() => onShowPapers?.(pack.school_id)}
                          sx={{ flex: 1, textTransform: "none", fontWeight: 700, bgcolor: "#1a1a1a", "&:hover": { bgcolor: "#333" } }}
                        >
                          Show its papers
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={downloading === pack.id}
                          startIcon={downloading === pack.id ? <CircularProgress size={14} /> : <DownloadIcon />}
                          onClick={() => downloadAll(pack)}
                          sx={{ textTransform: "none", fontWeight: 700 }}
                        >
                          Download PDFs
                        </Button>
                      </>
                    ) : (
                      <Button
                        fullWidth
                        variant={inBasket(pack) ? "outlined" : "contained"}
                        size="small"
                        disabled={inBasket(pack)}
                        startIcon={<ShoppingCart />}
                        onClick={() =>
                          onAddToBasket?.({
                            resourceType: "bundle",
                            resourceId: pack.id,
                            name: pack.name,
                            price: Number(String(pack.price ?? "").replace(/[^0-9.]/g, "")) || 0,
                            priceDisplay: pack.price_display || null,
                          })
                        }
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          ...(inBasket(pack) ? {} : { background: "linear-gradient(90deg, #7c3aed, #6d28d9)" }),
                        }}
                      >
                        {inBasket(pack) ? "In basket" : "Add to basket"}
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
